import { AnyAction } from '@reduxjs/toolkit'

import type { FileType } from './types'
import {
  ADD_ERROR,
  CLEAR_ALL_ERRORS,
  CLEAR_ERROR,
  CREATE_IMAGE_PREVIEWS,
  DISPLAY_IN_MODAL,
  FIND_AUTHUNAUTH,
  ON_TRACKING_ERROR,
  SAVE_IMAGE_PREVIEW,
  SHOW_ALL_CATEGORIES,
  TOGGLE_CATEGORY_PREVIEW,
  UPLOAD_CLEAR,
  UPLOAD_CLEAR_MOST,
  UPLOAD_DELETE,
  UPLOAD_FAILURE,
  UPLOAD_FINISHED,
  UPLOAD_PROGRESS,
  UPLOAD_REQUESTING,
  UPLOAD_SELECT_CATEGORY,
  UPLOAD_SELECT_HANDLER,
  UPLOAD_SUCCESS
} from './upload-constants'

type UploadError = {
  type?: string
  name: string
  error: string
}

type UploadState = {
  requesting: boolean
  success: boolean
  finished: boolean
  progress: number
  files: FileType[]
  hideCategories: string[]
  errors: UploadError[]
  category: string
  trackingError: boolean
  sourceSystem: string
  handler: string
}

const initState: UploadState = {
  requesting: false,
  success: false,
  finished: false,
  progress: 0,
  files: [],
  hideCategories: [],
  errors: [],
  category: '',
  trackingError: false,
  sourceSystem: '',
  handler: ''
}

const upload = (state = initState, action: AnyAction) => {
  switch(action.type) {
    case CREATE_IMAGE_PREVIEWS:
      return { ...state, files: [...state.files, ...action.files] }
    case UPLOAD_SELECT_CATEGORY:
      return { ...state, category: action.category }
    case UPLOAD_DELETE:
      return { ...state, files: state.files.filter(file => file.id !== action.id) }
    case UPLOAD_CLEAR:
      return { ...state, requesting: false, success: false, finished: false, files: [], errors: [] }
    case UPLOAD_CLEAR_MOST:
      return {
        ...state,
        requesting: false,
        success: false,
        finished: false,
        errors: [],
        files: state.files.reduce<FileType[]>((a,c) => {
          if (typeof c.error !== 'undefined' && c.error.message !== '400') {
            return [
              ...a,
              {
                ...c,
                error: undefined,
                progress: '0',
                timeLeft: undefined
              }
            ]
          }
          return a
        }, [])
      }
    case UPLOAD_REQUESTING:
      return { ...state, requesting: true, success: false }
    case UPLOAD_SUCCESS:
      return { ...state, requesting: false, success: true }
    case UPLOAD_FINISHED:
      return { ...state, finished: true, requesting: false }
    case UPLOAD_SELECT_HANDLER:
      return { ...state, handler: action.handler }
    case UPLOAD_PROGRESS:
      return {
        ...state,
        files: state.files.map(file => {
          if (file.file.name === action.filename) {
            return {
              ...file,
              progress: action.progress,
              timeLeft: action.timeLeft,
              trackingId: action.trackingId,
              batchId: action.batchId
            }
          }
          return file
        })
      }
    case UPLOAD_FAILURE:
      return {
        ...state,
        files: state.files.map(file => {
          if (file.file.name === action.filename) {
            return {
              ...file,
              error: action.error,
              trackingId: action.trackingId,
              batchId: action.batchId
            }
          }
          return file
        })
      }
    case SAVE_IMAGE_PREVIEW:
      return {
        ...state,
        files: state.files.map(file => {
          if (file.file.name === action.filename) {
            return { ...file, src: action.src, mimeType: action.mimeType }
          }
          return file
        })
      }
    case DISPLAY_IN_MODAL:
      return {
        ...state,
        files: state.files.map(file =>
          ({ ...file, showInModal: action.filename === file.file.name })
        )
      }
    case TOGGLE_CATEGORY_PREVIEW: {
      const hideCategories = state.hideCategories.includes(action.category)
        ? state.hideCategories.filter(c => c !== action.category)
        : [...state.hideCategories, action.category]
      return { ...state, hideCategories }
    }
    case SHOW_ALL_CATEGORIES:
      return { ...state, hideCategories: [] }
    case FIND_AUTHUNAUTH:
      return { ...state, sourceSystem: action.sourceSystem }
    case ON_TRACKING_ERROR:
      return { ...state, trackingError: action.trackingError }
    default:
      return state
  }
}

type UploadErrorsState = UploadError[]

const uploadErrors = (state: UploadErrorsState = [], action: AnyAction) => {
  switch(action.type) {
    case ADD_ERROR:
      return [...state, { name: action.name, error: action.error }]
    case CLEAR_ERROR:
      return state.filter(e => e.name !== action.name)
    case CLEAR_ALL_ERRORS:
      return []
    default: return state
  }
}

export {
  initState,
  upload,
  uploadErrors
}

export type {
  UploadErrorsState,
  UploadState
}
